import React, { createContext, useContext } from "react";
import { useFormContext } from "react-hook-form";
import cn from "classnames";
import styles from "./FormControl.module.scss";

interface FormControlContextType {
  name: string;
  error?: string;
}

const FormControlContext = createContext<FormControlContextType | undefined>(
  undefined,
);

export const useFormControl = () => {
  const context = useContext(FormControlContext);
  if (!context) {
    throw new Error(
      "useFormControl must be used within a FormControl component",
    );
  }
  return context;
};

interface FormControlProps {
  /** The field name (used for registration with react-hook-form) */
  name: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * FormControl component
 *
 * Wrapper component that provides context to its children about
 * the form field name and error state.
 */
const FormControl: React.FC<FormControlProps> = ({
  name,
  children,
  className,
}) => {
  const {
    formState: { errors },
  } = useFormContext();

  const fieldError =
    errors[name] && typeof errors[name].message === "string"
      ? errors[name].message
      : undefined;

  return (
    <FormControlContext.Provider value={{ name, error: fieldError }}>
      <div className={cn(styles.formControl, className)}>{children}</div>
    </FormControlContext.Provider>
  );
};

export default FormControl;
